import React, { ReactNode } from 'react';
import { ErrorBoundary } from './ErrorBoundary';
import { useCore } from '../hooks/useCore';

interface ErrorBoundaryWrapperProps {
  children: ReactNode;
  region?: string;
  level?: 'page' | 'section';
}

/**
 * ErrorBoundaryLayout - Wraps route content with Error Boundary
 * Provides consistent error handling for page/route sections
 *
 * Usage in routes:
 * {
 *   path: '/subscription',
 *   element: (
 *     <ErrorBoundaryLayout region="subscription">
 *       <YourLayout>
 *         <Outlet />
 *       </YourLayout>
 *     </ErrorBoundaryLayout>
 *   ),
 *   children: [...]
 * }
 */
export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  region = 'unknown',
  level = 'page',
}) => {
  const { gotoPtEApp } = useCore();
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error(`[${region}] ${level}-level error:`, error, errorInfo);
  };

  return (
    <ErrorBoundary
      onError={handleError}
      fallback={
        level === 'page' ? undefined : (
          <div className='flex items-center justify-center p-8 bg-yellow-50 rounded-lg border border-yellow-200 my-4'>
            <div className='text-center max-w-sm'>
              <div className='text-4xl mb-2'>⚠️</div>
              <h3 className='text-lg font-bold text-yellow-900 mb-2'>
                Error in {region}
              </h3>
              <p className='text-sm text-yellow-800'>
                Something went wrong in this section. Please try refreshing the
                page.
              </p>
            </div>
          </div>
        )
      }
      gotoPtEApp={gotoPtEApp}
    >
      {children}
    </ErrorBoundary>
  );
};
